import React, { useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { toast } from 'react-toastify';
import { useGetDiscountCoupon } from '@/hooks/useGetDiscountCoupon';
import { useShoppingCartStore } from '@/store/shoppingCart/shoppingCartStore';
import { validateCouponAgainstCartItems } from '@/helpers/couponValidation';
import { Format } from '@/helpers/formatters';
import { calculateEffectiveDiscount } from '@/helpers/utils';
import { makeAccessProfile } from '@/helpers/accessProfileUtil';
import { useUserProfile } from '@/hooks/useUserData';

const pulse = keyframes`
    0% {
        box-shadow: 0 0 0 0 rgba(255, 193, 7, 0.4);
    }
    70% {
        box-shadow: 0 0 0 6px rgba(255, 193, 7, 0);
    }
    100% {
        box-shadow: 0 0 0 0 rgba(255, 193, 7, 0);
    }
`;

const CouponContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    background-color: #f5f5f5;
    border-radius: 8px;
    margin: 16px 0;
`;

const CouponInputStyled = styled.input`
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    flex: 1;
    
    &:focus {
        outline: none;
        border-color: #007bff;
        box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
    }
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 8px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
    padding: 10px 16px;
    border: none;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: 'Hero New';
    
    ${props => 
        props.variant === 'secondary' ? css`
            background-color: #e9ecef;
            color: #495057;
            
            &:hover {
                background-color: #dee2e6;
                transform: scale(1.02);
            }
            
            &:active {
                transform: scale(0.98);
            }
        ` : css`
            background-color: #C4C4C4;
            color: #09090B;
            transform: scale(1);
            
            &:hover {
                background-color: #FFC107;
                color: #09090B;
                transform: scale(1.035);
                animation: ${pulse} 0.6s ease-out;
            }
            
            &:active {
                transform: scale(0.96);
            }
        `}
    
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: scale(1);
    }
`;

const CouponInfo = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    background-color: #d4edda;
    border-radius: 4px;
    border: 1px solid #c3e6cb;
    color: #155724;
`;

const Label = styled.label`
    font-size: 14px;
    font-weight: 600;
    color: #333;
`;

export const CouponInput: React.FC = () => {
    const [couponCode, setCouponCode] = useState('');
    const { mutate, isPending } = useGetDiscountCoupon();
    const coupon = useShoppingCartStore(state => state.coupon);
    const setCouponAction = useShoppingCartStore(state => state.setCoupon);
    const removeCouponAction = useShoppingCartStore(state => state.removeCoupon);
    const cartValues = useShoppingCartStore(state => state.cartValues);
    const user = useUserProfile();

    // Verificar se o perfil do usuário deve ocultar o input
    const userProfile = makeAccessProfile(user.data);
    const shouldHideCouponInput = [
        'proprietário',
        'Proprietário TAC',
        'dependente',
        'Dependente TAC',
    ].includes(userProfile);

    if (shouldHideCouponInput) {
        return null; // Não renderiza o componente
    }

    // Calcular desconto efetivo
    const cartTotal = useShoppingCartStore.getState().cartValues?.reduce(
        (acc, product) => acc + product.value,
        0
    ) ?? 0;
    const effectiveDiscount = calculateEffectiveDiscount(coupon, cartTotal);

    const handleApplyCoupon = () => {
        if (!couponCode.trim()) {
            return;
        }
        mutate(couponCode, {
            onSuccess: (data) => {
                // Validar regras do cupom contra o carrinho usando helper central
                const validation = validateCouponAgainstCartItems(data, cartValues);
                if (!validation.isValid) {
                    toast.error(validation.errorMessage || 'Cupom inválido para o carrinho');
                    return;
                }

                setCouponAction(data);
                setCouponCode('');
                
                // Toast de sucesso após aplicar o cupom
                const discountDisplay = data.percentage 
                    ? `${data.percentage}%` 
                    : `R$ ${Number(data.discountValue).toFixed(2)}`;
                toast.success(`Cupom aplicado! Desconto: ${discountDisplay}`);
            },
        });
    };

    const handleRemoveCoupon = () => {
        removeCouponAction();
        setCouponCode('');
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleApplyCoupon();
        }
    };

    return (
        <CouponContainer>
            <Label>Cupom de desconto</Label>
            {coupon ? (
                <CouponInfo>
                    <div>
                        <strong>Cupom aplicado!</strong> Desconto de{' '}
                        <strong>{Format.currency(effectiveDiscount)}</strong>
                    </div>
                    <Button variant="secondary" onClick={handleRemoveCoupon}>
                        Remover
                    </Button>
                </CouponInfo>
            ) : (
                <ButtonGroup>
                    <CouponInputStyled
                        type="text"
                        placeholder="Digite seu cupom"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        onKeyPress={handleKeyPress}
                        disabled={isPending}
                    />
                    <Button
                        onClick={handleApplyCoupon}
                        disabled={!couponCode.trim() || isPending}
                    >
                        {isPending ? 'Verificando...' : 'Aplicar'}
                    </Button>
                </ButtonGroup>
            )}
        </CouponContainer>
    );
};
